/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  AppConfigProvider,
  buildBundledResources,
  initI18n,
  SupportedLocales,
  fireGtmEvent,
  addGtmScript,
  fireGtmEventforSRR,
} from '@airasia-rn/ui';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { HomeErrorBoundary } from '../components/HomeErrorBoundary';
import {
  createNextLocaleRedirect,
  createWebLogger,
  getCookie,
  getTranslationsVersion,
  loadMoengageSdkForTesting,
} from '@airasia-rn/web/utils';
import { WebConfigProvider } from '@airasia-rn/web/config';
import { WebPushOptIn } from '@airasia-rn/web/components';
import { commonWebLocales } from '@airasia-rn/web/locale';
import { Logger } from '@airasia-rn/core-logger';
import { FlightConfigProvider } from '@airasia-rn/flights';
import { locales as flightsLocales } from '@airasia-rn/flights/locales';
import {
  HomepageProvider,
  HomeConfigProvider,
  homepageLocales,
} from '@airasia-rn/home';
import { locales as accountLocales } from '@airasia-rn/account/locales';
import { HotelConfigProvider } from '@airasia-rn/hotels/configs/HotelConfigProvider';
import { HotelIntegrationsProvider } from '@airasia-rn/hotels/configs/HotelIntegrationsProvider';
import { locales as hotelsLocales } from '@airasia-rn/hotels/locales';
import { SnapConfigProvider } from '@airasia-rn/snap/configs/SnapConfigProvider';
import { SnapIntegrationsProvider } from '@airasia-rn/snap/configs/SnapIntegrationsProvider';
import { locales as snapLocales } from '@airasia-rn/snap/locales';
import {
  SnapNavigationContext,
  buildSnapListingUrl,
  resolveSnapHotelsListHref,
  type SearchFormState,
} from '@airasia-rn/snap';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useRouter, useSearchParams } from 'next/navigation';
import { snapIntegrations } from './snapIntegrations';

// Every input is a module-level import, so this deep-merge is constant. Built
// once at module scope: called from the component body it ran on every render
// attempt (~6 per load, ~7ms each) and returned a new 156kB object each time,
// which also broke the identity of the `bundledTranslations` prop downstream.
const bundledResources = buildBundledResources(
  homepageLocales,
  flightsLocales,
  hotelsLocales,
  snapLocales,
  commonWebLocales,
  accountLocales,
);

const env = process.env.NEXT_PUBLIC_ENV as 'prd' | 'stg';
const onlyBundledTranslations = process.env.NODE_ENV === 'development';
const translationVersion = getTranslationsVersion(10);

initI18n(env, onlyBundledTranslations, translationVersion, bundledResources);

type HomepagePlatform = 'web' | 'web-mobile';

export default function Providers({
  children,
  locale,
  geoId,
  currency,
  hostname,
  platform = 'web',
}: {
  children: ReactNode;
  locale: string;
  geoId?: string;
  currency: string;
  hostname: string;
  /** Homepage dataset the server prefetched (from the User-Agent hint). */
  platform?: HomepagePlatform;
}) {
  const nextRouter = useRouter();
  const nextSearchParams = useSearchParams();
  const [activeLocale, setActiveLocale] = useState(locale);

  useEffect(() => {
    setActiveLocale(locale);
  }, [locale]);

  const handleLocaleChange = useMemo(
    () =>
      createNextLocaleRedirect({
        shallow: true,
        onLocaleSelect: (newLocale) => {
          setActiveLocale(newLocale);
        },
      }),
    [],
  );

  const searchParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (nextSearchParams) {
      nextSearchParams.forEach((value, key) => {
        params[key] = value;
      });
    }
    return params;
  }, [nextSearchParams]);

  const snapNavigation = useMemo(
    () => ({
      navigateBack: () => nextRouter.back(),
      navigateToHotelsList: (queryString: string) => {
        // Host adapter only — URL shape is resolved in @airasia-rn/snap
        // (same pattern as hotels' resolveListNavigationHref).
        if (typeof window === 'undefined') return;
        window.location.assign(resolveSnapHotelsListHref(queryString));
      },
      navigateToResultPage: (state: SearchFormState) => {
        const fullUrl = buildSnapListingUrl({
          state,
          env,
          countryCode: geoId ?? 'MY',
          currency: currency ?? 'MYR',
          language: activeLocale ?? 'en',
        });
        window.location.assign(fullUrl);
      },
      handleRedirection: () => {},
      searchParams,
    }),
    [nextRouter, env, geoId, currency, activeLocale, searchParams],
  );
  const GA_COOKIE_NAME: string = '_ga';

  const [gaClientId, setGaClientId] = useState<string | null>(null);

  useEffect(() => {
    const cookieGaId = getCookie(GA_COOKIE_NAME);
    setGaClientId(cookieGaId ?? null);
  }, []);

  // TEMPORARY: test-only MoEngage load while the GTM tag is unavailable.
  // No-op in prd or without NEXT_PUBLIC_MOENGAGE_APP_ID.
  useEffect(() => {
    loadMoengageSdkForTesting();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (['localhost', '127.0.0.1'].includes(window.location.hostname)) return;

    const runGtm = () => {
      addGtmScript('GTM-WTQCST')
        .then(() => fireGtmEventforSRR())
        .catch((error) => console.error('GTM script failed to load:', error));
    };

    if (document.readyState === 'complete') {
      runGtm();
    } else {
      window.addEventListener('load', runGtm);
      return () => window.removeEventListener('load', runGtm);
    }
  }, []);

  const webLogger = useMemo(() => {
    const baseLogger: Logger = {
      logEvent: (event, params) => fireGtmEvent({ event, ...params }),
      logError: (error, context) =>
        fireGtmEvent({
          event: 'error',
          errorMessage: error instanceof Error ? error.message : String(error),
          ...context,
        }),
      logScreenView: (screen, params) =>
        fireGtmEvent({ event: screen, ...params }),
    };
    return createWebLogger(baseLogger);
  }, []);

  const flightsChannelHash = process.env.NEXT_PUBLIC_FLIGHTS_CHANNEL_HASH ?? '';
  const flightsConsumerId = process.env.NEXT_PUBLIC_FLIGHTS_CONSUMER_ID ?? '';
  const hotelsClientId = process.env.NEXT_PUBLIC_HOTELS_SA_CLIENT_ID ?? '';
  const hotelsJwtHash = process.env.NEXT_PUBLIC_HOTELS_JWT_HASH ?? '';
  const hotelsXorKey = process.env.NEXT_PUBLIC_HOTELS_XOR_KEY ?? '';
  const snapLfcChannelHash =
    process.env.NEXT_PUBLIC_SNAP_LFC_CHANNEL_HASH ?? '';
  const askBoScriptId = process.env.NEXT_PUBLIC_ASKBO_CHATBOT_SCRIPT_ID ?? '';
  const askBoScriptUrl = process.env.NEXT_PUBLIC_ASKBO_CHATBOT_SCRIPT_URL ?? '';

  const FLIGHTS_ANONYMOUS_SSO = {
    accessToken:
      process.env.NEXT_PUBLIC_FLIGHTS_ANONYMOUS_SSO_ACCESS_TOKEN ?? '',
    refreshToken:
      process.env.NEXT_PUBLIC_FLIGHTS_ANONYMOUS_SSO_REFRESH_TOKEN ?? '',
    userId: process.env.NEXT_PUBLIC_FLIGHTS_ANONYMOUS_SSO_USER_ID ?? '',
  } as const;

  const zeroMetrics = {
    frame: { x: 0, y: 0, width: 0, height: 0 },
    insets: { top: 0, right: 0, bottom: 0, left: 0 },
  };

  return (
    <AppConfigProvider
      appName="home-web"
      env={env}
      locale={activeLocale as SupportedLocales}
      currency={currency}
      appVersion="1.0.0"
      deviceId="web"
      geoId={geoId}
      bundledTranslations={bundledResources}
      translationsVersion={translationVersion}
      onlyBundledTranslations={onlyBundledTranslations}
      onLocaleChange={handleLocaleChange}
      logger={webLogger}
    >
      <FlightConfigProvider
        channelHash={flightsChannelHash}
        consumerId={flightsConsumerId}
        gaId={gaClientId}
        anonymousSso={FLIGHTS_ANONYMOUS_SSO}
        geoId={geoId}
        env={env}
        hostname={hostname}
      >
        <HotelConfigProvider
          clientId={hotelsClientId}
          jwtHash={hotelsJwtHash}
          xorKey={hotelsXorKey}
        >
          <HotelIntegrationsProvider integrations={{}}>
            <SnapNavigationContext.Provider value={snapNavigation}>
              <SnapConfigProvider
                geoId={geoId}
                lfcChannelHash={snapLfcChannelHash}
              >
                <SnapIntegrationsProvider integrations={snapIntegrations}>
                  <WebConfigProvider
                    askBoConfig={{
                      scriptId: askBoScriptId,
                      scriptUrl: askBoScriptUrl,
                    }}
                  >
                    <HomeConfigProvider
                      homeCosmosApi={process.env.NEXT_PUBLIC_HOME_COSMOS_API}
                      homeCosmosApiKey={
                        process.env.NEXT_PUBLIC_HOME_COSMOS_API_KEY
                      }
                      homeTripPlannerApi={
                        process.env.NEXT_PUBLIC_HOME_TRIP_PLANNER_API
                      }
                      homeRewardsApi={process.env.NEXT_PUBLIC_HOME_REWARDS_API}
                      homeBigpayApi={process.env.NEXT_PUBLIC_HOME_BIGPAY_API}
                    >
                      <HomepageProvider config={{ forcedPlatform: platform }}>
                        <SafeAreaProvider initialMetrics={zeroMetrics}>
                          <BottomSheetModalProvider>
                            <HomeErrorBoundary
                              locale={activeLocale}
                              currency={currency}
                              geoId={geoId}
                            >
                              {children}
                            </HomeErrorBoundary>
                            <WebPushOptIn
                              onAllow={() =>
                                webLogger.logEvent('web_push_opt_in_accepted', {
                                  action: 'tap',
                                  category: 'web_push',
                                })
                              }
                              onDismiss={() =>
                                webLogger.logEvent(
                                  'web_push_opt_in_dismissed',
                                  {
                                    action: 'tap',
                                    category: 'web_push',
                                  },
                                )
                              }
                              onError={(error) =>
                                webLogger.logError(error.message, {
                                  category: 'web_push',
                                })
                              }
                            />
                          </BottomSheetModalProvider>
                        </SafeAreaProvider>
                      </HomepageProvider>
                    </HomeConfigProvider>
                  </WebConfigProvider>
                </SnapIntegrationsProvider>
              </SnapConfigProvider>
            </SnapNavigationContext.Provider>
          </HotelIntegrationsProvider>
        </HotelConfigProvider>
      </FlightConfigProvider>
    </AppConfigProvider>
  );
}
